/** Каркас приложения: боковое меню, верхняя панель, область контента. */

import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";

import { useProcesses, useRequestsToApprove, useUnreadMail, useUnreadSupplierChats } from "./api/hooks";
import { usePendingAwardApprovals } from "./api/itemBoardHooks";
import { hasRole, useAuth, type Role } from "./auth/AuthProvider";
import DiscussionBell from "./components/DiscussionBell";
import { PageErrorBoundary } from "./components/ErrorBoundary";
import { useDiscussionUnread } from "./components/RequestDiscussion";
import { canDecideProcurement, useProcurementGroupId } from "./lib/procurementDecision";
import CommandPalette, {
  useCommandPalette,
  type PaletteDestination,
} from "./components/CommandPalette";
import Icon, { type IconName } from "./components/Icon";
import { ROLE_DESCRIPTION, ROLE_LABEL } from "./lib/labels";
import { useRealtime } from "./realtime/RealtimeProvider";
import { useTheme } from "./theme/ThemeProvider";
import { Avatar, Counter, IconButton, Menu, StatusDot, Tooltip } from "./ui";
import { BrandMark, useBrand } from "./brand/BrandProvider";

/* ==========================================================================
   Карта навигации
   ========================================================================== */

type NavGroupKey = "work" | "reference" | "admin";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  roles: Role[];
  group: NavGroupKey;
  /** Дополнительные слова для командной палитры. */
  keywords?: string;
  /** Виден только с доступом к отчётам (выдаёт администратор). */
  needsReports?: boolean;
}

const ALL: Role[] = ["purchaser", "approver", "analyst", "admin", "operator"];

const NAV: NavItem[] = [
  // --- работа ---
  { to: "/", label: "Дашборд", icon: "dashboard", roles: ALL, group: "work", keywords: "главная сводка аналитика" },
  { to: "/requests", label: "Мои заявки", icon: "document", roles: ["operator", "admin"], group: "work", keywords: "заявка потребность" },
  { to: "/purchase-requests", label: "Заявки", icon: "inbox", roles: ["purchaser", "admin"], group: "work", keywords: "согласованные выполненные отменённые взять в работу" },
  { to: "/processes", label: "Закупки", icon: "processes", roles: ["purchaser", "approver", "analyst", "admin"], group: "work", keywords: "процессы закупок список" },
  // Шаг маршрута можно адресовать любой роли (кладовщику, аналитику) —
  // значит, и очередь согласований нужна любой внутренней роли.
  { to: "/approvals", label: "Согласования", icon: "approval", roles: ["purchaser", "approver", "admin", "operator", "analyst", "warehouse"], group: "work", keywords: "утверждение решение" },
  // Кладовщик приходит сюда из «Приёмки» за согласованием — и обратно.
  { to: "/warehouse", label: "Приёмка", icon: "inbox", roles: ["warehouse"], group: "work", keywords: "склад поставка" },
  { to: "/portal-items", label: "Позиции портала", icon: "send", roles: ["purchaser", "admin"], group: "work", keywords: "публикация кп оферты" },
  { to: "/reports", label: "Отчёты", icon: "chart", roles: ALL, group: "work", keywords: "аналитика расходы реестр выгрузка excel загрузка закупщиков", needsReports: true },
  { to: "/tenders", label: "Тендеры", icon: "compare", roles: ["purchaser", "approver", "admin"], group: "work", keywords: "торги сравнение предложений" },

  // --- справочники и связь ---
  { to: "/mail", label: "Почта", icon: "mail", roles: ["purchaser", "approver", "admin"], group: "reference", keywords: "письма входящие" },
  { to: "/chat", label: "AI-ассистент", icon: "sparkles", roles: ALL, group: "reference", keywords: "чат помощник" },
  { to: "/suppliers", label: "Поставщики", icon: "building", roles: ["purchaser", "admin"], group: "reference", keywords: "контрагенты реестр" },
  { to: "/audit", label: "Аудит-лог", icon: "history", roles: ["analyst", "admin"], group: "reference", keywords: "события история" },

  // --- администрирование ---
  { to: "/admin", label: "Обзор системы", icon: "sliders", roles: ["admin"], group: "admin" },
  { to: "/users", label: "Пользователи", icon: "users", roles: ["admin"], group: "admin" },
  { to: "/departments", label: "Отделы", icon: "briefcase", roles: ["purchaser", "approver", "admin"], group: "admin" },
  { to: "/admin/product-categories", label: "Теги и категории", icon: "tag", roles: ["admin"], group: "admin" },
  { to: "/admin/units", label: "Единицы измерения", icon: "ruler", roles: ["admin"], group: "admin" },
  { to: "/admin/approval-routes", label: "Маршруты согласования", icon: "route", roles: ["admin"], group: "admin" },
  { to: "/admin/objects", label: "Объекты", icon: "building", roles: ["admin"], group: "admin", keywords: "санаторий площадка филиал" },
  { to: "/admin/process-templates", label: "Процессы закупок", icon: "layers", roles: ["admin"], group: "admin", keywords: "шаблон блоки стадии планка" },
  { to: "/admin/ai-routes", label: "ИИ-маршруты закупок", icon: "layers", roles: ["admin"], group: "admin" },
  { to: "/admin/llm", label: "Настройки ИИ", icon: "sparkles", roles: ["admin"], group: "admin" },
  { to: "/email-accounts", label: "Email-учётки", icon: "inbox", roles: ["admin"], group: "admin" },
];

const GROUP_LABEL: Record<NavGroupKey, string> = {
  work: "Работа",
  reference: "Справочники",
  admin: "Администрирование",
};

const PALETTE_DESTINATIONS: PaletteDestination[] = NAV.map((item) => ({
  to: item.to,
  label: item.label,
  icon: item.icon,
  group: GROUP_LABEL[item.group],
  roles: item.roles,
  keywords: item.keywords,
  needsReports: item.needsReports,
}));

/** Заголовок верхней панели по текущему маршруту. */
function currentSectionTitle(pathname: string): string {
  const exact = NAV.find((item) => item.to === pathname);
  if (exact) return exact.label;
  const nested = NAV.filter((item) => item.to !== "/" && pathname.startsWith(item.to)).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
  return nested?.label ?? "";
}

/* ==========================================================================
   Каркас
   ========================================================================== */

export default function App() {
  const { connected } = useRealtime();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const palette = useCommandPalette();
  const [navOpen, setNavOpen] = useState(false);

  const role: Role = (user?.role as Role) ?? "purchaser";

  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  // Поставщики работают в отдельном портале.
  if (role === "supplier") return <Navigate to="/portal" replace />;

  const visible = NAV.filter(
    (item) => item.roles.includes(role) && (!item.needsReports || Boolean(user?.reports_access)),
  );

  return (
    <div className="flex min-h-screen bg-canvas">
      {/* Обход меню: до содержимого иначе двадцать нажатий Tab через все
          разделы. Ссылка не видна, пока не получит фокус. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-toast focus:rounded-md focus:border focus:border-line focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ink focus:shadow-lg"
      >
        Перейти к содержимому
      </a>

      {/* ---------- боковое меню ---------- */}
      <aside className="sticky top-0 hidden h-screen w-sidebar shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <SidebarContent
          items={visible}
          user={user}
          onLogout={logout}
          onOpenPalette={() => palette.setOpen(true)}
        />
      </aside>

      {/* ---------- меню на узком экране ---------- */}
      {navOpen && (
        <div className="fixed inset-0 z-drawer lg:hidden">
          <div className="absolute inset-0 bg-overlay/45" onClick={() => setNavOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-[17rem] max-w-[86vw] animate-fade-in flex-col border-r border-line bg-surface shadow-xl">
            <SidebarContent
              items={visible}
              user={user}
              onLogout={logout}
              onOpenPalette={() => {
                setNavOpen(false);
                palette.setOpen(true);
              }}
              onClose={() => setNavOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* ---------- контент ---------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-topbar flex h-topbar items-center gap-2 border-b border-line bg-surface/85 px-3 backdrop-blur-md sm:px-5">
          <IconButton
            icon="menu"
            label="Открыть меню"
            className="lg:hidden"
            onClick={() => setNavOpen(true)}
          />

          {/* Не heading: единственный h1 страницы задаёт сама страница. */}
          <span className="truncate text-sm font-semibold text-ink">
            {currentSectionTitle(pathname)}
          </span>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => palette.setOpen(true)}
            className="hidden h-8 items-center gap-2 rounded-md border border-line bg-sunken/60 pl-2.5 pr-1.5 text-sm text-faint transition-colors hover:border-line-strong hover:text-muted sm:flex"
          >
            <Icon name="search" size={14} />
            <span>Поиск</span>
            <kbd className="kbd ml-4">⌘K</kbd>
          </button>

          <ConnectionBadge connected={connected} />
          <DiscussionBell />
          <ThemeToggle />
        </header>

        <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
          <div key={pathname} className="mx-auto w-full max-w-[100rem] animate-fade-in px-4 py-5 sm:px-6 lg:px-8">
            <PageErrorBoundary>
              <Outlet />
            </PageErrorBoundary>
          </div>
        </main>
      </div>

      <CommandPalette
        open={palette.open}
        onClose={() => palette.setOpen(false)}
        destinations={PALETTE_DESTINATIONS}
      />
    </div>
  );
}

/* ==========================================================================
   Содержимое бокового меню
   ========================================================================== */

function SidebarContent({
  items,
  user,
  onLogout,
  onOpenPalette,
  onClose,
}: {
  items: NavItem[];
  user: ReturnType<typeof useAuth>["user"];
  onLogout: () => void;
  onOpenPalette: () => void;
  onClose?: () => void;
}) {
  const groups: NavGroupKey[] = ["work", "reference", "admin"];
  const brand = useBrand();

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-3">
        <Link to="/" className="flex min-w-0 items-center gap-2" data-testid="brand-header">
          <BrandMark size="sm" />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold leading-tight text-ink" data-testid="brand-header-name">
              {brand.name}
            </span>
            <span className="block truncate text-2xs leading-tight text-faint">
              {brand.tagline}
            </span>
          </span>
        </Link>
        <div className="flex-1" />
        {onClose && <IconButton icon="close" label="Закрыть меню" size="sm" onClick={onClose} />}
      </div>

      <div className="px-2 pb-2">
        <button
          type="button"
          onClick={onOpenPalette}
          className="flex h-8 w-full items-center gap-2 rounded-md border border-line bg-sunken/60 px-2.5 text-sm text-faint transition-colors hover:border-line-strong hover:text-muted"
        >
          <Icon name="search" size={14} />
          <span className="flex-1 text-left">Поиск</span>
          <kbd className="kbd">⌘K</kbd>
        </button>
      </div>

      <nav aria-label="Основная навигация" className="flex-1 overflow-y-auto scrollbar-thin px-2 pb-2">
        {groups.map((group) => {
          const groupItems = items.filter((item) => item.group === group);
          if (groupItems.length === 0) return null;
          return (
            <div key={group} className="mb-1 mt-2 first:mt-0">
              <div className="px-2 pb-1 text-2xs font-medium uppercase tracking-wider text-faint">
                {GROUP_LABEL[group]}
              </div>
              <ul className="space-y-px">
                {groupItems.map((item) => (
                  <li key={item.to}>
                    <NavItemLink item={item} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {user && <UserCard user={user} onLogout={onLogout} />}
    </>
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/" || item.to === "/admin"}
      className={({ isActive }) =>
        clsx(
          "group flex h-8 items-center gap-2.5 rounded-md px-2 text-sm transition-colors",
          isActive
            ? "bg-accent-soft font-medium text-accent"
            : "text-muted hover:bg-sunken hover:text-ink",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            name={item.icon}
            size={16}
            className={isActive ? "text-accent" : "text-faint group-hover:text-muted"}
          />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.to === "/approvals" && <ApprovalsBadge />}
          {item.to === "/requests" && <AwaitingAnswerBadge />}
          {item.to === "/processes" && <SupplierChatsBadge />}
          {item.to === "/mail" && <MailBadge />}
        </>
      )}
    </NavLink>
  );
}

/* ==========================================================================
   Индикаторы
   ========================================================================== */

function ConnectionBadge({ connected }: { connected: boolean }) {
  return (
    <Tooltip content={connected ? "Обновления приходят в реальном времени" : "Нет связи с сервером обновлений"}>
      <span
        data-testid="realtime-status"
        data-connected={connected ? "true" : "false"}
        className="flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted"
      >
        <StatusDot tone={connected ? "success" : "neutral"} pulse={connected} />
        <span className="hidden sm:inline">{connected ? "онлайн" : "офлайн"}</span>
      </span>
    </Tooltip>
  );
}

function ThemeToggle() {
  const { theme, choice, setChoice } = useTheme();
  return (
    <Menu
      align="end"
      items={[
        { label: "Светлая", icon: "sun", onSelect: () => setChoice("light") },
        { label: "Тёмная", icon: "moon", onSelect: () => setChoice("dark") },
        { label: "Как в системе", icon: "monitor", onSelect: () => setChoice("system") },
      ]}
      trigger={({ toggle }) => (
        <IconButton
          icon={choice === "system" ? "monitor" : theme === "dark" ? "moon" : "sun"}
          label="Тема оформления"
          onClick={toggle}
        />
      )}
    />
  );
}

/** Ожидающие решения: закупки + заявки + победители.
 *
 *  Закупщику раньше не горело ничего: очередь запрашивалась по
 *  `approver_id = я`, а закупщика согласующим почти не назначают — счётчик
 *  всегда выходил нулевым, хотя на самой странице закупки были видны.
 *  Теперь закупщик, как и админ, видит всю очередь, а в счёт идут только
 *  те закупки, по которым он может решить (`canDecideProcurement`) — цифра
 *  в меню совпадает с цифрой на вкладке «Закупки». */
function ApprovalsBadge() {
  const { user } = useAuth();
  // Закупки и победители — у закупщика, согласующего и админа; прочим
  // ролям шаг маршрута может достаться только по заявке.
  const procurementGroupId = useProcurementGroupId();
  const procurementRoles = procurementGroupId
    ? user?.role === "admin" || Boolean(user?.approval_group_ids?.includes(procurementGroupId))
    : hasRole(user, "purchaser", "approver", "admin");
  const seesWholeQueue = user?.role === "admin" || user?.role === "purchaser" || Boolean(procurementGroupId);
  const processes = useProcesses(
    seesWholeQueue
      ? { status: "waiting_user_review", limit: 100 }
      : { status: "waiting_user_review", approver_id: user?.id, limit: 100 },
    { enabled: procurementRoles },
  );
  const requests = useRequestsToApprove({ limit: 100 });
  const awards = usePendingAwardApprovals(procurementRoles);
  const decidable = (processes.data?.items || []).filter((p) =>
    canDecideProcurement(user, p, procurementGroupId),
  ).length;
  const count =
    (procurementRoles ? decidable : 0) +
    (requests.data?.length || 0) +
    (procurementRoles ? awards.data?.length || 0 : 0);
  if (!user || count === 0) return null;
  return <Counter value={count} tone="danger" />;
}

/** «Мои заявки»: сколько вопросов по моим заявкам ждут моего ответа. */
function AwaitingAnswerBadge() {
  const unread = useDiscussionUnread();
  const count = (unread.data ?? []).filter((u) => u.awaiting_me).length;
  if (count === 0) return null;
  return <Counter value={count} tone="danger" />;
}

/** Непрочитанные сообщения от поставщиков. */
function SupplierChatsBadge() {
  const unread = useUnreadSupplierChats();
  return <Counter value={unread.data?.total || 0} tone="danger" />;
}

/** Неразобранные входящие письма. Ящик снабжения общий, поэтому счётчик
 *  общий: письмо, открытое одним закупщиком, уходит из счётчика у всех. */
function MailBadge() {
  const unread = useUnreadMail();
  return <Counter value={unread.data?.total || 0} tone="danger" />;
}

/* ==========================================================================
   Карточка пользователя
   ========================================================================== */

function UserCard({
  user,
  onLogout,
}: {
  user: { full_name: string; role: Role; department_name: string | null; email: string };
  onLogout: () => void;
}) {
  return (
    <div className="border-t border-line p-2">
      <Menu
        align="start"
        side="top"
        className="w-full"
        items={[
          { label: ROLE_DESCRIPTION[user.role], icon: "info", disabled: true },
          { label: "Выйти", icon: "logout", danger: true, separated: true, onSelect: onLogout },
        ]}
        trigger={({ toggle }) => (
          <button
            type="button"
            onClick={toggle}
            className="flex w-full items-center gap-2.5 rounded-md p-1.5 text-left transition-colors hover:bg-sunken"
          >
            <Avatar name={user.full_name} size={28} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium leading-tight text-ink">
                {user.full_name}
              </span>
              <span className="block truncate text-2xs leading-tight text-faint">
                {ROLE_LABEL[user.role]}
                {user.department_name && ` · ${user.department_name}`}
              </span>
            </span>
            <Icon name="chevronUpDown" size={14} className="shrink-0 text-faint" />
          </button>
        )}
      />
    </div>
  );
}
